import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import EventIcon from '@mui/icons-material/Event';
import HubIcon from '@mui/icons-material/Hub';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MovieIcon from '@mui/icons-material/Movie';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { FrontendNotebook } from '../../contexts/NotebooksContext';
import type { NewsCluster, WikiConcept } from '../../types/types';
import { inferNotebookDraftKind } from '../../utils/draftNotebookKind';

export type LibraryLinkedContentRailProps = {
  mode: 'notebook' | 'podcast';
  subjectId: string;
  readOnly: boolean;
  linkedNotebookIds: string[];
  onLinkedNotebookIdsChange: (next: string[]) => Promise<void>;
  allNotebooks: FrontendNotebook[];
  activeNotebooks: FrontendNotebook[];
  /** Clusters stored on this draft (Insights / entities). */
  linkedClusters: NewsCluster[];
  onOpenClusterDetails?: (event: React.MouseEvent, cluster: NewsCluster) => void;
  /** Remove a cluster that was attached directly to this draft (not inherited). */
  onRemoveCluster?: (clusterTitle: string) => Promise<boolean>;
  /** Podcast: cluster id strings from generation metadata. */
  podcastClusterIds?: string[];
  /** Extra wiki entities stored on the article draft (beyond cluster-derived mentions). */
  draftLinkedWikiEntities?: WikiConcept[];
  onDraftLinkedWikiEntitiesChange?: (next: WikiConcept[]) => Promise<void>;
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    fontSize: '0.8125rem',
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
};

/** Popper renders in a portal; style `paper` so option text is not light-on-white. */
const autocompletePaperSx = {
  bgcolor: '#18181b',
  color: '#e4e4e7',
  border: '1px solid #3f3f46',
  boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
  '& .MuiAutocomplete-listbox': {
    bgcolor: '#18181b',
    py: 0.5,
  },
  '& .MuiAutocomplete-option': {
    minHeight: 40,
    color: '#fafafa',
  },
  '& .MuiAutocomplete-option.Mui-focused': {
    bgcolor: 'rgba(96, 165, 250, 0.14)',
  },
  '& .MuiAutocomplete-option[aria-selected="true"]': {
    bgcolor: 'rgba(96, 165, 250, 0.22)',
  },
  '& .MuiAutocomplete-groupUl .MuiAutocomplete-option': {
    pl: 2,
  },
  '& .MuiAutocomplete-groupLabel': {
    bgcolor: '#27272a',
    color: '#d4d4d8',
    fontSize: '0.7rem',
    fontWeight: 700,
    px: 1.5,
    py: 0.75,
    borderBottom: '1px solid #3f3f46',
  },
} as const;

function linkGroupLabel(nb: FrontendNotebook): string {
  const k = inferNotebookDraftKind(nb);
  switch (k) {
    case 'article':
      return 'Article drafts';
    case 'recipe':
      return 'Recipes';
    case 'movie':
      return 'Movies';
    case 'event':
      return 'Events';
    default:
      return 'Library items';
  }
}

function isLinkableLibraryNotebook(nb: FrontendNotebook): boolean {
  const k = inferNotebookDraftKind(nb);
  return k === 'article' || k === 'recipe' || k === 'movie' || k === 'event';
}

/** Non-empty notebook title for display and search (name or draft title). */
function notebookDisplayTitle(nb: FrontendNotebook): string {
  return (nb.name || '').trim() || (nb.draft?.title || '').trim();
}

function hasNotebookDisplayTitle(nb: FrontendNotebook): boolean {
  return notebookDisplayTitle(nb).length > 0;
}

function kindIconForNotebook(nb: FrontendNotebook) {
  const k = inferNotebookDraftKind(nb);
  const sx = { fontSize: 18, color: '#a1a1aa', flexShrink: 0 } as const;
  switch (k) {
    case 'article':
      return <ArticleIcon sx={sx} titleAccess="Article draft" />;
    case 'recipe':
      return <RestaurantIcon sx={sx} titleAccess="Recipe" />;
    case 'movie':
      return <MovieIcon sx={sx} titleAccess="Movie" />;
    case 'event':
      return <EventIcon sx={sx} titleAccess="Event" />;
    default:
      return <ArticleIcon sx={sx} titleAccess="Library item" />;
  }
}

function draftPublicationChip(nb: FrontendNotebook): { label: string; sx: Record<string, unknown> } {
  const ps = nb.draft?.publication_status;
  const rfp = nb.draft?.ready_for_publishing;
  if (ps === 'published') {
    return { label: 'Published', sx: { height: 22, fontSize: '0.65rem', bgcolor: '#14532d', color: '#bbf7d0' } };
  }
  if (ps === 'scheduled') {
    return { label: 'Scheduled', sx: { height: 22, fontSize: '0.65rem', bgcolor: '#0c4a6e', color: '#bae6fd' } };
  }
  if (ps === 'ready_to_publish' || rfp) {
    return { label: 'Ready to publish', sx: { height: 22, fontSize: '0.65rem', bgcolor: '#713f12', color: '#fef08a' } };
  }
  return { label: 'Draft', sx: { height: 22, fontSize: '0.65rem', bgcolor: '#27272a', color: '#d4d4d8' } };
}

type WikiEntityKind = WikiConcept['wiki_type'];

const ENTITY_KIND_TABS: { value: WikiEntityKind; title: string; searchWord: string }[] = [
  { value: 'persons', title: 'People', searchWord: 'people' },
  { value: 'organizations', title: 'Organizations', searchWord: 'organizations' },
  { value: 'locations', title: 'Places', searchWord: 'places' },
];

function conceptKey(c: WikiConcept): string {
  return `${c.wiki_type}\u0000${(c.concept || '').trim().toLowerCase()}`;
}

function dedupeWikiConcepts(items: WikiConcept[]): WikiConcept[] {
  const m = new Map<string, WikiConcept>();
  for (const c of items) {
    const concept = (c.concept || '').trim();
    if (!concept) continue;
    const k = conceptKey({ ...c, concept });
    if (!m.has(k)) {
      m.set(k, { wiki_type: c.wiki_type, concept, wiki_url: c.wiki_url || '' });
    }
  }
  return Array.from(m.values());
}

function flattenClusterWikiConcepts(clusters: NewsCluster[]): WikiConcept[] {
  const out: WikiConcept[] = [];
  for (const cl of clusters) {
    for (const w of cl.wiki_concepts || []) {
      if (w?.concept?.trim()) {
        out.push({
          wiki_type: w.wiki_type,
          concept: w.concept.trim(),
          wiki_url: w.wiki_url || '',
        });
      }
    }
  }
  return out;
}

function isKnownWikiType(t: string): t is WikiEntityKind {
  return t === 'persons' || t === 'organizations' || t === 'locations';
}

const wikiEntityChipSx = {
  height: 26,
  borderRadius: 0,
  bgcolor: '#18181b',
  color: '#e4e4e7',
  border: '1px solid #3f3f46',
  '& .MuiChip-label': { px: 0.75, fontSize: '0.72rem' },
} as const;

function iconGlyphForWikiKind(kind: WikiEntityKind) {
  const sx = { fontSize: 22, color: 'inherit', display: 'block' } as const;
  if (kind === 'persons') {
    return <PersonOutlineIcon sx={sx} />;
  }
  if (kind === 'organizations') {
    return <BusinessOutlinedIcon sx={sx} />;
  }
  return <PlaceOutlinedIcon sx={sx} />;
}

function iconForWikiKind(kind: WikiEntityKind) {
  const wrap = (node: React.ReactNode) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, flexShrink: 0 }}>{node}</Box>
  );
  if (kind === 'persons') {
    return wrap(<PersonOutlineIcon sx={{ fontSize: 18, color: '#a78bfa' }} />);
  }
  if (kind === 'organizations') {
    return wrap(<BusinessOutlinedIcon sx={{ fontSize: 18, color: '#38bdf8' }} />);
  }
  return wrap(<PlaceOutlinedIcon sx={{ fontSize: 18, color: '#4ade80' }} />);
}

export function LibraryLinkedContentRail({
  mode,
  subjectId,
  readOnly,
  linkedNotebookIds,
  onLinkedNotebookIdsChange,
  allNotebooks,
  activeNotebooks,
  linkedClusters,
  onOpenClusterDetails,
  onRemoveCluster,
  podcastClusterIds = [],
  draftLinkedWikiEntities = [],
  onDraftLinkedWikiEntitiesChange,
}: LibraryLinkedContentRailProps) {
  const [linkedSubTab, setLinkedSubTab] = useState(0);
  const [pick, setPick] = useState<FrontendNotebook | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkSearchInput, setLinkSearchInput] = useState('');
  const [entityKindTab, setEntityKindTab] = useState<WikiEntityKind>('persons');
  const [entitySearchQuery, setEntitySearchQuery] = useState('');
  const [localManualWiki, setLocalManualWiki] = useState<WikiConcept[]>([]);

  const persistedManualWiki = Boolean(onDraftLinkedWikiEntitiesChange);
  const manualWikiList = persistedManualWiki ? draftLinkedWikiEntities : localManualWiki;

  useEffect(() => {
    setLinkedSubTab(0);
    setPick(null);
    setLinkSearchInput('');
    setEntityKindTab('persons');
    setEntitySearchQuery('');
    setLocalManualWiki([]);
  }, [subjectId]);

  const inheritedClustersFromLinkedNotebooks = useMemo(() => {
    const byTitle = new Map<string, NewsCluster>();
    for (const lid of linkedNotebookIds) {
      const nb = allNotebooks.find((n) => n.id === lid);
      if (!nb?.clusters?.length) continue;
      for (const cl of nb.clusters) {
        if (!byTitle.has(cl.title)) {
          byTitle.set(cl.title, {
            ...cl,
            articles: cl.articles || [],
            suggested_titles: cl.suggested_titles || [],
            social_media: cl.social_media || [],
            contrasting_views: cl.contrasting_views || [],
            wiki_concepts: cl.wiki_concepts || [],
            timeline: cl.timeline || [],
          });
        }
      }
    }
    return Array.from(byTitle.values());
  }, [linkedNotebookIds, allNotebooks]);

  const inheritedTitles = useMemo(
    () => new Set(inheritedClustersFromLinkedNotebooks.map((c) => c.title)),
    [inheritedClustersFromLinkedNotebooks]
  );

  const directDraftClusters = useMemo(
    () => linkedClusters.filter((c) => !inheritedTitles.has(c.title)),
    [linkedClusters, inheritedTitles]
  );

  const draftLinkOptions = useMemo(() => {
    return activeNotebooks.filter((nb) => {
      if (nb.id === subjectId) return false;
      if (!isLinkableLibraryNotebook(nb)) return false;
      if (nb.status !== 'active') return false;
      if (linkedNotebookIds.includes(nb.id)) return false;
      if (!hasNotebookDisplayTitle(nb)) return false;
      return true;
    });
  }, [activeNotebooks, subjectId, linkedNotebookIds]);

  useEffect(() => {
    if (!pick) return;
    if (!draftLinkOptions.some((nb) => nb.id === pick.id)) {
      setPick(null);
    }
  }, [pick, draftLinkOptions]);

  const autocompleteKey = useMemo(
    () => `${subjectId}:${[...linkedNotebookIds].sort().join(',')}`,
    [subjectId, linkedNotebookIds]
  );

  const attachPick = useCallback(async () => {
    if (readOnly || !pick || !hasNotebookDisplayTitle(pick)) return;
    setBusy(true);
    try {
      const next = [...linkedNotebookIds, pick.id];
      await onLinkedNotebookIdsChange(next);
      setPick(null);
      setLinkSearchInput('');
    } finally {
      setBusy(false);
    }
  }, [readOnly, pick, linkedNotebookIds, onLinkedNotebookIdsChange]);

  const linkedLibraryRows = useMemo(
    () =>
      linkedNotebookIds.map((id) => ({
        id,
        notebook:
          allNotebooks.find((n) => n.id === id) || activeNotebooks.find((n) => n.id === id) || null,
      })),
    [linkedNotebookIds, allNotebooks, activeNotebooks]
  );

  const unlinkNotebook = useCallback(
    async (id: string) => {
      if (readOnly) return;
      setBusy(true);
      try {
        await onLinkedNotebookIdsChange(linkedNotebookIds.filter((x) => x !== id));
      } finally {
        setBusy(false);
      }
    },
    [readOnly, linkedNotebookIds, onLinkedNotebookIdsChange]
  );

  const linkedNotebookClustersFlat = useMemo(() => {
    const clusters: NewsCluster[] = [];
    for (const lid of linkedNotebookIds) {
      const nb = allNotebooks.find((n) => n.id === lid) || activeNotebooks.find((n) => n.id === lid);
      if (!nb?.clusters?.length) continue;
      clusters.push(...nb.clusters);
    }
    return clusters;
  }, [linkedNotebookIds, allNotebooks, activeNotebooks]);

  const identifiedFromDraftClusters = useMemo(() => {
    return dedupeWikiConcepts(flattenClusterWikiConcepts(linkedClusters)).filter((c) =>
      isKnownWikiType(c.wiki_type)
    );
  }, [linkedClusters]);

  const discoveryWikiPool = useMemo(() => {
    return dedupeWikiConcepts([
      ...flattenClusterWikiConcepts(linkedClusters),
      ...flattenClusterWikiConcepts(linkedNotebookClustersFlat),
    ]).filter((c) => isKnownWikiType(c.wiki_type));
  }, [linkedClusters, linkedNotebookClustersFlat]);

  const claimedConceptKeys = useMemo(() => {
    const s = new Set<string>();
    for (const c of identifiedFromDraftClusters) s.add(conceptKey(c));
    for (const c of manualWikiList) s.add(conceptKey(c));
    return s;
  }, [identifiedFromDraftClusters, manualWikiList]);

  const entitySearchHits = useMemo(() => {
    const q = entitySearchQuery.trim().toLowerCase();
    return discoveryWikiPool
      .filter((c) => c.wiki_type === entityKindTab)
      .filter((c) => !claimedConceptKeys.has(conceptKey(c)))
      .filter((c) => !q || c.concept.toLowerCase().includes(q))
      .slice(0, 100);
  }, [discoveryWikiPool, entityKindTab, entitySearchQuery, claimedConceptKeys]);

  const identifiedForKindTab = useMemo(
    () => identifiedFromDraftClusters.filter((c) => c.wiki_type === entityKindTab),
    [identifiedFromDraftClusters, entityKindTab]
  );

  const manualForKindTab = useMemo(
    () => manualWikiList.filter((c) => c.wiki_type === entityKindTab),
    [manualWikiList, entityKindTab]
  );

  const applyManualWikiList = useCallback(
    async (next: WikiConcept[]) => {
      const deduped = dedupeWikiConcepts(next);
      if (persistedManualWiki && onDraftLinkedWikiEntitiesChange) {
        await onDraftLinkedWikiEntitiesChange(deduped);
      } else {
        setLocalManualWiki(deduped);
      }
    },
    [persistedManualWiki, onDraftLinkedWikiEntitiesChange]
  );

  const addWikiCandidate = useCallback(
    async (c: WikiConcept) => {
      if (readOnly) return;
      const k = conceptKey(c);
      if (claimedConceptKeys.has(k)) return;
      await applyManualWikiList([...manualWikiList, c]);
    },
    [readOnly, claimedConceptKeys, manualWikiList, applyManualWikiList]
  );

  const removeManualWiki = useCallback(
    async (c: WikiConcept) => {
      if (readOnly) return;
      const k = conceptKey(c);
      await applyManualWikiList(manualWikiList.filter((x) => conceptKey(x) !== k));
    },
    [readOnly, manualWikiList, applyManualWikiList]
  );

  const removeCluster = useCallback(
    async (title: string) => {
      if (readOnly || !onRemoveCluster) return;
      setBusy(true);
      try {
        await onRemoveCluster(title);
      } finally {
        setBusy(false);
      }
    },
    [readOnly, onRemoveCluster]
  );

  const renderClusterRow = (
    cluster: NewsCluster,
    index: number,
    listLength: number,
    variant: 'inherited' | 'direct'
  ) => {
    const isInherited = variant === 'inherited';
    const showDelete = !isInherited && !readOnly && Boolean(onRemoveCluster);
    const showEye = Boolean(onOpenClusterDetails);
    const actionGutter: string | number = showDelete ? '100px' : showEye ? '52px' : 1.5;

    return (
      <ListItem
        key={`${cluster.title}-${variant}`}
        sx={{
          bgcolor: '#0a0a0a',
          mb: 1,
          borderRadius: 1,
          border: '1px solid #27272a',
          py: 0.75,
          pr: actionGutter,
          boxSizing: 'border-box',
        }}
        divider={index < listLength - 1}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'nowrap' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  minWidth: 0,
                  flex: '1 1 auto',
                  overflow: 'hidden',
                }}
              >
                <HubIcon sx={{ fontSize: 18, color: '#60a5fa', flexShrink: 0 }} titleAccess="Cluster" />
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  sx={{ color: '#fafafa', fontSize: '0.85rem', minWidth: 0, flex: '1 1 0%' }}
                >
                  {cluster.title}
                </Typography>
              </Box>
              {isInherited ? (
                <Tooltip title="Read-only (from a notebook linked in the header)" arrow>
                  <LockOutlinedIcon sx={{ fontSize: 16, color: '#71717a', flexShrink: 0 }} />
                </Tooltip>
              ) : null}
            </Box>
          }
          secondary={(cluster.general_overview || '').substring(0, 120)}
          secondaryTypographyProps={{
            sx: {
              color: '#a1a1aa',
              fontSize: '0.7rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            },
          }}
        />
        {(showEye || showDelete) && (
          <ListItemSecondaryAction>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {showEye ? (
                <IconButton
                  size="small"
                  onClick={(e) => onOpenClusterDetails?.(e, cluster)}
                  sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              ) : null}
              {showDelete ? (
                <Tooltip title="Unlink cluster from this draft" arrow>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Unlink cluster from this draft"
                      disabled={busy}
                      onClick={() => void removeCluster(cluster.title)}
                      sx={{ color: '#a1a1aa', '&:hover': { color: '#f87171' } }}
                    >
                      <LinkOffIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
          </ListItemSecondaryAction>
        )}
      </ListItem>
    );
  };

  const hasAnyClusters =
    inheritedClustersFromLinkedNotebooks.length > 0 || directDraftClusters.length > 0;

  const showLinkedContentList =
    mode === 'notebook' || mode === 'podcast' || linkedNotebookIds.length > 0 || hasAnyClusters;

  const linkedContentScrollBlock =
    showLinkedContentList ? (
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', mt: 1.5, pr: 0.25 }}>
        {linkedNotebookIds.length > 0 ? (
          <List dense disablePadding sx={{ mb: (mode === 'notebook' || mode === 'podcast') && hasAnyClusters ? 1.5 : 0 }}>
            {linkedLibraryRows.map(({ id, notebook }) => {
              const showUnlink = !readOnly;
              const titleText = notebook ? notebookDisplayTitle(notebook) || id : `Notebook ${id.slice(0, 8)}…`;
              const chip = notebook ? draftPublicationChip(notebook) : null;
              return (
                <ListItem
                  key={id}
                  sx={{
                    bgcolor: '#0a0a0a',
                    mb: 1,
                    borderRadius: 1,
                    border: '1px solid #27272a',
                    py: 0.75,
                    pr: showUnlink ? '52px' : 1.5,
                    boxSizing: 'border-box',
                  }}
                  divider={false}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          minWidth: 0,
                          flexWrap: 'nowrap',
                        }}
                      >
                        {notebook ? (
                          kindIconForNotebook(notebook)
                        ) : (
                          <ArticleIcon sx={{ fontSize: 18, color: '#52525b', flexShrink: 0 }} />
                        )}
                        <Typography
                          component="span"
                          variant="body2"
                          noWrap
                          sx={{ color: '#fafafa', fontSize: '0.85rem', minWidth: 0, flex: '1 1 0%' }}
                        >
                          {titleText}
                        </Typography>
                        {chip ? <Chip size="small" label={chip.label} sx={chip.sx} /> : null}
                      </Box>
                    }
                  />
                  {showUnlink ? (
                    <ListItemSecondaryAction>
                      <Tooltip title="Unlink from this draft" arrow>
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Unlink notebook"
                            disabled={busy}
                            onClick={() => void unlinkNotebook(id)}
                            sx={{ color: '#a1a1aa', '&:hover': { color: '#f87171' } }}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        ) : null}

        {linkedNotebookIds.length > 0 && (mode === 'notebook' || mode === 'podcast') && hasAnyClusters ? (
          <Divider sx={{ mb: 1.5, borderColor: '#27272a' }} />
        ) : null}

        {(mode === 'notebook' || mode === 'podcast') && hasAnyClusters ? (
          <List dense disablePadding>
            {inheritedClustersFromLinkedNotebooks.map((cluster, index) =>
              renderClusterRow(cluster, index, inheritedClustersFromLinkedNotebooks.length, 'inherited')
            )}
            {inheritedClustersFromLinkedNotebooks.length > 0 && directDraftClusters.length > 0 ? (
              <Divider sx={{ my: 1, borderColor: '#27272a' }} />
            ) : null}
            {directDraftClusters.map((cluster, index) =>
              renderClusterRow(cluster, index, directDraftClusters.length, 'direct')
            )}
          </List>
        ) : null}

        {(mode === 'notebook' || mode === 'podcast') && linkedNotebookIds.length === 0 && !hasAnyClusters ? (
          <Typography variant="caption" sx={{ color: '#52525b', display: 'block' }}>
            No linked library items or clusters yet.
          </Typography>
        ) : null}
      </Box>
    ) : null;

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Tabs
        value={linkedSubTab}
        onChange={(_, v) => setLinkedSubTab(v)}
        sx={{
          alignSelf: 'stretch',
          flexShrink: 0,
          minHeight: 36,
          mb: 0.5,
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': { color: '#a1a1aa', minHeight: 36, fontSize: '0.7rem' },
          '& .Mui-selected': { color: '#fafafa' },
          '& .MuiTabs-indicator': { backgroundColor: '#34d399' },
        }}
      >
        <Tab label="Linked content" />
        <Tab label="Linked entities" />
      </Tabs>

      {linkedSubTab === 0 && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!readOnly && (
            <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Autocomplete<FrontendNotebook, false, false, false>
                key={autocompleteKey}
                componentsProps={{ paper: { sx: autocompletePaperSx } }}
                size="small"
                options={draftLinkOptions}
                groupBy={(nb) => linkGroupLabel(nb)}
                value={pick}
                onChange={(_, v) => setPick(v)}
                inputValue={linkSearchInput}
                onInputChange={(_, v, reason) => {
                  if (reason === 'reset') {
                    setLinkSearchInput('');
                    return;
                  }
                  setLinkSearchInput(v);
                }}
                blurOnSelect
                getOptionLabel={(nb) => notebookDisplayTitle(nb)}
                getOptionDisabled={(nb) => linkedNotebookIds.includes(nb.id) || nb.id === subjectId}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(opts, state) => {
                  const notLinked = opts.filter(
                    (nb) => !linkedNotebookIds.includes(nb.id) && hasNotebookDisplayTitle(nb)
                  );
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return notLinked.slice(0, 50);
                  return notLinked
                    .filter((nb) => notebookDisplayTitle(nb).toLowerCase().includes(q))
                    .slice(0, 60);
                }}
                renderOption={(props, nb) => {
                  const chip = draftPublicationChip(nb);
                  return (
                    <li {...props}>
                      <Box
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 0.25 }}
                      >
                        {kindIconForNotebook(nb)}
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, color: '#fafafa' }} noWrap>
                          {notebookDisplayTitle(nb)}
                        </Typography>
                        <Chip size="small" label={chip.label} sx={chip.sx} />
                      </Box>
                    </li>
                  );
                }}
                ListboxProps={{ style: { maxHeight: 280 } }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search drafts, recipes, movies, events…" sx={fieldSx} />
                )}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                disabled={!pick || busy}
                onClick={() => void attachPick()}
                sx={{
                  borderColor: '#3f3f46',
                  color: '#e4e4e7',
                  '&:hover': { borderColor: '#34d399', bgcolor: 'rgba(52,211,153,0.08)' },
                }}
              >
                Link selection
              </Button>
            </Box>
          )}

          {linkedContentScrollBlock}

        </Box>
      )}

      {linkedSubTab === 1 && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#71717a', display: 'block' }}>
            People, organizations, and places detected on this draft (from its clusters), plus any you add from
            linked notebooks and their clusters.
          </Typography>

          <ToggleButtonGroup
            exclusive
            value={entityKindTab}
            onChange={(_, v: WikiEntityKind | null) => {
              if (v) setEntityKindTab(v);
            }}
            fullWidth
            size="small"
            sx={{
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                py: 1,
                px: 0,
                color: '#a1a1aa',
                borderColor: '#3f3f46',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
              '& .MuiToggleButton-root.Mui-selected': {
                color: '#fafafa',
                bgcolor: 'rgba(52, 211, 153, 0.12)',
              },
            }}
          >
            {ENTITY_KIND_TABS.map((t) => (
              <ToggleButton key={t.value} value={t.value} aria-label={t.title} title={t.title}>
                {iconGlyphForWikiKind(t.value)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            size="small"
            value={entitySearchQuery}
            onChange={(e) => setEntitySearchQuery(e.target.value)}
            placeholder={`Search ${ENTITY_KIND_TABS.find((x) => x.value === entityKindTab)?.searchWord ?? 'entities'} from linked notebooks & clusters…`}
            disabled={readOnly}
            sx={fieldSx}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 0.75 }}>
              On this draft
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', minHeight: 32 }}>
              {identifiedForKindTab.length === 0 ? (
                <Typography variant="caption" sx={{ color: '#52525b' }}>
                  None in this category yet.
                </Typography>
              ) : (
                identifiedForKindTab.map((c, i) => (
                  <Chip
                    key={`id-${entityKindTab}-${i}-${c.concept}`}
                    icon={iconForWikiKind(c.wiki_type)}
                    label={c.concept}
                    size="small"
                    onClick={c.wiki_url ? () => window.open(c.wiki_url, '_blank', 'noopener,noreferrer') : undefined}
                    sx={{
                      ...wikiEntityChipSx,
                      cursor: c.wiki_url ? 'pointer' : 'default',
                      '& .MuiChip-icon': { ml: 0.5 },
                    }}
                  />
                ))
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 0.75 }}>
              Added for this draft
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', minHeight: 32 }}>
              {manualForKindTab.length === 0 ? (
                <Typography variant="caption" sx={{ color: '#52525b' }}>
                  {readOnly ? 'No manually linked entities.' : 'Pick matches from the list below.'}
                </Typography>
              ) : (
                manualForKindTab.map((c, i) => (
                  <Chip
                    key={`man-${entityKindTab}-${i}-${c.concept}`}
                    icon={iconForWikiKind(c.wiki_type)}
                    label={c.concept}
                    size="small"
                    onClick={c.wiki_url ? () => window.open(c.wiki_url, '_blank', 'noopener,noreferrer') : undefined}
                    onDelete={
                      readOnly
                        ? undefined
                        : () => {
                            void removeManualWiki(c);
                          }
                    }
                    deleteIcon={<LinkOffIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      ...wikiEntityChipSx,
                      cursor: c.wiki_url ? 'pointer' : 'default',
                      '& .MuiChip-icon': { ml: 0.5 },
                    }}
                  />
                ))
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#27272a' }} />

          <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
            <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 0.75 }}>
              From linked content
            </Typography>
            {entitySearchHits.length === 0 ? (
              <Typography variant="caption" sx={{ color: '#52525b', display: 'block' }}>
                None in this category yet.
              </Typography>
            ) : (
              <List dense disablePadding>
                {entitySearchHits.map((c, i) => (
                  <ListItem
                    key={`hit-${entityKindTab}-${i}-${c.concept}`}
                    sx={{
                      border: '1px solid #27272a',
                      borderRadius: 1,
                      mb: 0.75,
                      py: 0.5,
                      pr: readOnly ? 1.5 : '48px',
                      bgcolor: '#0a0a0a',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          {iconForWikiKind(entityKindTab)}
                          <Typography variant="body2" sx={{ color: '#fafafa', fontSize: '0.8rem' }} noWrap>
                            {c.concept}
                          </Typography>
                        </Box>
                      }
                    />
                    {!readOnly ? (
                      <ListItemSecondaryAction>
                        <Tooltip title="Add to this draft" arrow>
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Add entity to draft"
                              disabled={busy}
                              onClick={() => void addWikiCandidate(c)}
                              sx={{ color: '#a1a1aa', '&:hover': { color: '#34d399' } }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    ) : null}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {mode === 'podcast' && podcastClusterIds.length > 0 ? (
            <Box>
              <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 0.5 }}>
                Episode generation cluster ids (metadata)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {podcastClusterIds.map((id) => (
                  <Chip key={id} size="small" label={id} sx={{ bgcolor: '#27272a', color: '#e4e4e7' }} />
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
